import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { authService } from '@/modules/auth/api/auth.service';
import { DASHBOARD_ROUTES, ADMIN_ROUTES } from '@/api/constants/app.url';
import { writeSessionCookie } from '@/shared/utils/cookies';
import { generateAuthToken, setAuthToken } from '@/shared/utils/auth-token';
import { setStoredPlan } from '@/shared/utils/stored-plan';
import { setStoredAccess } from '@/shared/utils/stored-access';
import { accessCacheKeys } from '@/shared/access/use-permissions';
import { sessionKeys } from '@/shared/hooks/use-session';
import { rememberAccount } from '@/shared/utils/account-book';
import type { ApiError } from '@/api';
import type { DevLoginRequest, DevLoginResponse, LoginPlan, LoginResult, ResendTwoFactorRequest, VerifyTwoFactorRequest } from '@/modules/auth/types';
import { isMfaChallenge } from '@/modules/auth/types';
import type { StoredAccess } from '@/shared/utils/stored-access';

/** Cache key for the currently-signed-in tenant's plan. Populated by login;
 *  components can read it via `useQuery({ queryKey: planCacheKeys.current() })`. */
export const planCacheKeys = {
    all: ['plan'] as const,
    current: () => [...planCacheKeys.all, 'current'] as const,
} as const;

/**
 * Sign-in mutation. Verifies the password against the API, then persists the
 * full server response so a page refresh keeps everything hydrated:
 *   - JWT  → localStorage (`ps_auth_token`) via `setAuthToken`
 *   - User + tenant id/slug/name/role → cookie + localStorage (`ps_session`)
 *     via `writeSessionCookie`
 *   - Plan → localStorage (`ps_plan`) via `setStoredPlan` AND query cache
 *     under `planCacheKeys.current()` for immediate component access
 */
/**
 * Persist a completed sign-in (token + session + plan + access) and redirect.
 * Shared by the password path (`useDevLogin`) and the 2FA path
 * (`useVerifyLoginTwoFactor`) so both establish an identical session.
 *
 * The account also goes into the account book, so the profile menu can switch
 * back to it later. `hardReload` is set when this sign-in ADDS an account
 * alongside one already signed in: every cached query in memory belongs to the
 * previous account, so the new session boots from a clean page rather than
 * inheriting them — the same reasoning as the workspace switcher's reload.
 */
function establishSession(
    data: any,
    qc: QueryClient,
    navigate: NavigateFunction,
    opts: { hardReload?: boolean } = {},
): void {
    const land = (to: string) => {
        if (opts.hardReload) window.location.replace(to);
        else navigate(to, { replace: true });
    };
    if (data.role === 'admin') {
        // Admin rides the same session cookie + bearer token as a tenant,
        // just with role 'super_admin'. ProtectedRoute gates the admin
        // routes on that role — there's no separate admin_token store.
        setAuthToken(data.token);
        const adminSession = {
            userId: data.user.id,
            email: data.user.email,
            fullName: data.user.full_name,
            tenantId: data.tenant.id,
            tenantSlug: data.tenant.slug,
            tenantName: data.tenant.name,
            role: data.tenant.role,
            isDevMode: false,
        };
        writeSessionCookie(adminSession);
        qc.setQueryData(sessionKeys.current(), adminSession);
        setStoredPlan(null);
        qc.setQueryData<LoginPlan | null>(planCacheKeys.current(), null);
        // Switchable like any other account (admins hold no plan/permissions).
        rememberAccount({ session: adminSession, token: data.token, plan: null, permissions: null });
        land(ADMIN_ROUTES.INSIGHT);
        return;
    }

    setAuthToken(data.token);

    const session = {
        userId: data.user.id,
        email: data.user.email,
        fullName: data.user.full_name,
        tenantId: data.tenant.id,
        tenantSlug: data.tenant.slug,
        tenantName: data.tenant.name,
        role: data.tenant.role,
        isDevMode: false,
    };
    writeSessionCookie(session);
    // Prime the session cache so the next render has the session in
    // hand — no flash of "signed out" before redirect.
    qc.setQueryData(sessionKeys.current(), session);
    setStoredPlan(data.plan);
    qc.setQueryData<LoginPlan | null>(planCacheKeys.current(), data.plan);

    const access: StoredAccess = { permissions: data.permissions };
    setStoredAccess(access);
    qc.setQueryData<StoredAccess>(accessCacheKeys.current(), access);

    // Keep this login in the account book, so the profile menu can switch back
    // to it after another account is added.
    rememberAccount({
        session,
        token: data.token,
        plan: data.plan ?? null,
        permissions: data.permissions ?? null,
    });

    land(DASHBOARD_ROUTES.HOME);
}

/** Options shared by both legs of sign-in. */
export interface SignInOptions {
    /** This sign-in is adding an account next to one already signed in. */
    addingAccount?: boolean;
}

export function useDevLogin(opts: SignInOptions = {}) {
    const qc = useQueryClient();
    const navigate = useNavigate();

    return useMutation<LoginResult, ApiError, DevLoginRequest>({
        mutationFn: authService.devLogin,
        onSuccess: (data) => {
            // 2FA-enabled accounts get a challenge instead of a session — the
            // sign-in form reads `mutation.data` and switches to the code step.
            if (isMfaChallenge(data)) return;
            establishSession(data, qc, navigate, { hardReload: opts.addingAccount });
        },
    });
}

/** Second leg of a 2FA sign-in: submit the code, then establish the session. */
export function useVerifyLoginTwoFactor(opts: SignInOptions = {}) {
    const qc = useQueryClient();
    const navigate = useNavigate();

    return useMutation<DevLoginResponse, ApiError, VerifyTwoFactorRequest>({
        mutationFn: authService.verifyTwoFactor,
        onSuccess: (data) => establishSession(data, qc, navigate, { hardReload: opts.addingAccount }),
    });
}

/** Mail a fresh security code during an email-method 2FA challenge. */
export function useResendLoginTwoFactor() {
    return useMutation<{ email: string }, ApiError, ResendTwoFactorRequest>({
        mutationFn: authService.resendTwoFactor,
    });
}

/**
 * Demo shortcut — sets the seeded "Traditions of Braselton" session without
 * any API call. Matches the legacy DevSignInForm's "Continue as Demo".
 */
export function useContinueAsDemo() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    return () => {
        const session = {
            userId: 'dev-user-kmi-founder',
            email: 'dev@kmi.local',
            fullName: 'KMI Founder',
            tenantId: 'dev-tenant-traditions',
            tenantSlug: 'traditions',
            tenantName: 'KMI Homes',
            role: 'tenant_admin' as const,
            isDevMode: true,
        };
        writeSessionCookie(session);
        generateAuthToken();
        setStoredPlan(null);
        qc.setQueryData(sessionKeys.current(), session);
        qc.setQueryData<LoginPlan | null>(planCacheKeys.current(), null);
        // Demo is a tenant_admin owner — null permissions = full access.
        const access: StoredAccess = { permissions: null };
        setStoredAccess(access);
        qc.setQueryData<StoredAccess>(accessCacheKeys.current(), access);
        navigate(DASHBOARD_ROUTES.HOME, { replace: true });
    };
}
